import {
  faArrowRight,
  faCheck,
  faCopy,
  faEllipsisVertical,
  faGear,
  faLink,
  faPlus,
  faSparkles,
  faTrash,
  faXmark,
} from "@fortawesome/pro-regular-svg-icons";
import {
  Button,
  Drawer,
  Dropdown,
  FieldGroup,
  Icon,
  Inline,
  Input,
  Label,
  Message,
  Section,
  Tooltip,
} from "@intility/bifrost-react";
import Select from "@intility/bifrost-react-select";
import { useState } from "react";
import {
  useClient,
  useMarkClientWiredUp,
  useUpdateRedirectUris,
} from "../api/clients";
import type { AppType, ClientListItem } from "../api/types";
import { buildManualSteps } from "../lib/wireUpManual";
import { buildWireUpPrompt, type WireUpClient } from "../lib/wireUpPrompt";
import { ClientWireUpSteps } from "./ClientWireUpSteps";
import { useCanEditClient } from "./useCanEditClient";

// The rules of RedirectUrisEditor (the client settings page), except the scheme:
// the select beside the input supplies that part, so the URL arrives whole.
// Returns the problem to show, or null when the URL is fine.
function redirectUrlProblem(url: string, existing: string[]): string | null {
  let parsed: URL | null = null;
  try {
    parsed = new URL(url);
  } catch {
    parsed = null;
  }
  if (parsed ? parsed.hash !== "" : url.includes("#")) {
    return "Redirect URLs must not contain a fragment (#).";
  }
  if (!(parsed && parsed.hostname.length > 0) && !url.includes("*")) {
    return "Redirect URL must have a valid host (e.g. https://example.com).";
  }
  if (existing.includes(url)) return "This redirect URL already exists.";
  return null;
}

// The scheme of a redirect URL, chosen in the select beside the input instead
// of typed.
const SCHEME_OPTIONS = [
  { value: "https://", label: "https://" },
  { value: "http://", label: "http://" },
];

// What the box says, per client kind. Only a SPA logs users in; every other
// kind authenticates. An API has two ways, the usual one first: check the
// tokens of users who logged in elsewhere, or act as itself like a worker.
const WIRE_UP: Record<AppType, { title: string; message: string }> = {
  spa: {
    title: "Wire up login",
    message: "Let users log in to this SPA with Intility ID.",
  },
  api: {
    title: "Wire up authentication",
    message: "Let this API validate user tokens, or authenticate as itself.",
  },
  worker: {
    title: "Wire up authentication",
    message: "Let this worker run as its own identity.",
  },
  custom: {
    title: "Wire up authentication",
    message: "Wire up authentication to connect your platform to Intility ID.",
  },
};

// The wire-up box every client gets: the message with its wire-up
// button, and, for SPA clients, the redirect URLs with an input to add more.
export function ClientWireUp({ client }: { client: ClientListItem }) {
  const isSpa = client.appType === "spa";
  // What this client's box says: login for a SPA, authentication for the rest.
  const wireUp = WIRE_UP[client.appType];
  const { data: detail } = useClient(client.id);
  const canEdit = useCanEditClient(client);
  const update = useUpdateRedirectUris();
  // The wired-up claim (ADR-0060): durable on the client, so it survives a
  // reload. The detail is the fresh source once loaded; the list item carries
  // it as the fallback while that loads.
  const wiredUp = (detail?.wiredUpAt ?? client.wiredUpAt) != null;
  const markWiredUp = useMarkClientWiredUp();
  function setWiredUp(value: boolean) {
    markWiredUp.mutate({ id: client.id, wiredUp: value });
  }
  const [newUrl, setNewUrl] = useState("");
  // The scheme of `newUrl`, held apart from it in the select.
  const [scheme, setScheme] = useState(SCHEME_OPTIONS[0]);
  const [problem, setProblem] = useState<string | null>(null);
  // The URL just copied, so its icon can show a check for a moment.
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);
  // Set from "Mark as complete" in the header menu or the drawer; the message
  // then says the client is wired up (see the claim above).
  // The drawer the "Wire up" buttons open, with the way of wiring up it shows:
  // auto setup is the default each time it opens.
  const [wireUpOpen, setWireUpOpenState] = useState(false);
  const [setupMode, setSetupMode] = useState<"auto" | "manual">("auto");
  function setWireUpOpen(open: boolean) {
    if (open) setSetupMode("auto");
    setWireUpOpenState(open);
  }
  // The AI prompt for auto setup, built from what the portal knows about
  // this client, and the copy button's feedback.
  const wireUpClient: WireUpClient | null = detail
    ? {
        name: detail.name,
        clientId: detail.clientId,
        appType: detail.appType,
        authMethod: detail.authMethod,
        redirectUris: detail.redirectUris,
        stack: detail.stack,
        environment:
          import.meta.env.VITE_ENVIRONMENT === "production"
            ? "production"
            : "development",
        mcpDocsUrl: `${window.location.origin}/docs/reference/mcp-server`,
        sdkDocsUrl: `${window.location.origin}/docs/reference/authentication-sdk`,
        environmentsDocsUrl: `${window.location.origin}/docs/getting-started/environments`,
      }
    : null;
  const prompt = wireUpClient ? buildWireUpPrompt(wireUpClient) : "";
  const [promptCopied, setPromptCopied] = useState(false);
  async function copyPrompt() {
    await navigator.clipboard.writeText(prompt);
    setPromptCopied(true);
    setTimeout(() => setPromptCopied(false), 2000);
  }

  const urls = detail?.redirectUris ?? [];
  const trimmed = newUrl.trim();
  // A SPA cannot be wired up before login has somewhere to send the user back.
  const missingRedirectUrl = isSpa && urls.length === 0;

  function addUrl() {
    if (!trimmed || !detail) return;
    const url = scheme.value + trimmed;
    const found = redirectUrlProblem(url, urls);
    setProblem(found);
    if (found) return;
    update.mutate(
      { id: client.id, redirectUris: [...urls, url] },
      { onSuccess: () => setNewUrl("") },
    );
  }

  function removeUrl(url: string) {
    update.mutate({
      id: client.id,
      redirectUris: urls.filter((u) => u !== url),
    });
  }

  async function copyUrl(url: string) {
    await navigator.clipboard.writeText(url);
    setCopiedUrl(url);
    setTimeout(() => setCopiedUrl((c) => (c === url ? null : c)), 2000);
  }

  return (
    <Section
      border
      radius
      background="base-3"
      className="client-object-redirects"
    >
      <Section.Header>
        <Inline align="center">
          <Icon icon={faLink} />
          <Inline.Stretch>{wireUp.title}</Inline.Stretch>
          <Dropdown
            placement="bottom-end"
            disabled={!canEdit}
            content={
              <>
                <button type="button" onClick={() => setWireUpOpen(true)}>
                  <Dropdown.Item icon={faLink}>Wire up</Dropdown.Item>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (!wiredUp && missingRedirectUrl) return;
                    setWiredUp(!wiredUp);
                  }}
                >
                  {/* Bifrost's tooltip on the item itself, shown on hover while
                      it is blocked. */}
                  <Tooltip
                    content="Missing redirect URL"
                    placement="left"
                    disabled={wiredUp || !missingRedirectUrl}
                  >
                    <Dropdown.Item
                      icon={wiredUp ? faXmark : faCheck}
                      state={
                        !wiredUp && missingRedirectUrl ? "inactive" : undefined
                      }
                    >
                      {wiredUp ? "Mark as incomplete" : "Mark as complete"}
                    </Dropdown.Item>
                  </Tooltip>
                </button>
              </>
            }
          >
            <Button
              variant="flat"
              small
              className="client-icon-button"
              aria-label="More"
              title="More"
              disabled={!canEdit}
            >
              <Icon icon={faEllipsisVertical} />
            </Button>
          </Dropdown>
        </Inline>
      </Section.Header>
      <Section.Content className="client-object-redirects-body">
        {wiredUp ? (
          <Message state="neutral">
            <Icon icon={faCheck} marginRight />
            Client is wired up
          </Message>
        ) : (
          // Yellow, like the "Platform not wired up" notice on the Clients tab,
          // and neutral with a disabled button when the user cannot edit the
          // client.
          <Message state={canEdit ? "warning" : "neutral"}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 16,
              }}
            >
              <span>{wireUp.message}</span>
              <Button
                variant="flat"
                small
                className={canEdit ? "client-wireup-button" : undefined}
                style={{ flexShrink: 0 }}
                disabled={!canEdit}
                onClick={() => setWireUpOpen(true)}
              >
                Wire up
                <Icon icon={faArrowRight} marginLeft />
              </Button>
            </div>
          </Message>
        )}
        {/* Redirect URLs belong to SPA clients only; every other client gets
            the box without them. */}
        {isSpa && (
          <>
            <Label>Redirect URL</Label>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 4,
                marginTop: -8,
                // The list scrolls past this height, so a client with many
                // URLs keeps the box the size of one with a few.
                maxHeight: 200,
                overflowY: "auto",
              }}
            >
              {urls.length > 0 ? (
                urls.map((url) => (
                  // Clicking anywhere on the row copies the URL; the copy icon is
                  // the keyboard way in, and the trash button stops the click.
                  // biome-ignore lint/a11y/noStaticElementInteractions: keyboard users get the copy button inside the row
                  // biome-ignore lint/a11y/useKeyWithClickEvents: see above
                  <div
                    key={url}
                    className="client-redirect-row"
                    onClick={() => copyUrl(url)}
                  >
                    <Button
                      variant="flat"
                      small
                      className="client-icon-button"
                      aria-label={`Copy ${url}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        copyUrl(url);
                      }}
                    >
                      <Icon icon={copiedUrl === url ? faCheck : faCopy} />
                    </Button>
                    <span className="client-redirect-url">{url}</span>
                    <Button
                      variant="flat"
                      small
                      className="client-icon-button"
                      aria-label={`Remove ${url}`}
                      disabled={!canEdit}
                      state={update.isPending ? "inactive" : "alert"}
                      onClick={(e) => {
                        e.stopPropagation();
                        removeUrl(url);
                      }}
                    >
                      <Icon icon={faTrash} />
                    </Button>
                  </div>
                ))
              ) : (
                <span
                  style={{ fontStyle: "italic", color: "var(--bfc-base-c-2)" }}
                >
                  No redirect URLs yet
                </span>
              )}
            </div>
            {/* Pinned to the bottom of the section, below the list. */}
            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: "flex-end",
                marginTop: "auto",
              }}
            >
              <div style={{ flex: 1 }}>
                <FieldGroup
                  state={problem ? "alert" : undefined}
                  feedback={problem ?? update.error?.message}
                >
                  <Select
                    label="URL scheme"
                    hideLabel
                    style={{ maxWidth: 120 }}
                    options={SCHEME_OPTIONS}
                    value={scheme}
                    onChange={(opt) => opt && setScheme(opt)}
                    isDisabled={!canEdit}
                  />
                  <Input
                    label="Add redirect URL"
                    hideLabel
                    value={newUrl}
                    onChange={(e) => {
                      let value = e.target.value;
                      // A pasted URL carries its own scheme: move it into the
                      // select, so the input keeps only the rest of the URL.
                      const pasted = value.match(/^https?:\/\//);
                      if (pasted) {
                        const option = SCHEME_OPTIONS.find(
                          (o) => o.value === pasted[0],
                        );
                        if (option) setScheme(option);
                        value = value.slice(pasted[0].length);
                      }
                      setNewUrl(value);
                      if (problem) setProblem(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addUrl();
                      }
                    }}
                    placeholder="your-app.example.com/callback"
                    disabled={!canEdit}
                  />
                </FieldGroup>
              </div>
              <Tooltip content="Add URL">
                <Button
                  className="client-icon-button"
                  aria-label="Add URL"
                  onClick={addUrl}
                  state={
                    update.isPending || !canEdit || !detail || !trimmed
                      ? "inactive"
                      : undefined
                  }
                >
                  <Icon icon={faPlus} />
                </Button>
              </Tooltip>
            </div>
          </>
        )}
      </Section.Content>

      <Drawer
        isOpen={wireUpOpen}
        onRequestClose={() => setWireUpOpen(false)}
        header={`Wire up ${client.name}`}
        footer={
          <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
            <Button variant="flat" onClick={() => setWireUpOpen(false)}>
              <Icon icon={faXmark} marginRight />
              Close
            </Button>
            <Tooltip
              content={missingRedirectUrl ? "Missing redirect URL" : ""}
              disabled={!missingRedirectUrl}
            >
              <Button
                variant="filled"
                state={missingRedirectUrl ? "inactive" : undefined}
                onClick={() => {
                  if (missingRedirectUrl) return;
                  setWiredUp(true);
                  setWireUpOpen(false);
                }}
              >
                <Icon icon={faCheck} marginRight />
                Mark as complete
              </Button>
            </Tooltip>
          </div>
        }
      >
        {markWiredUp.isError && (
          <Message state="alert" header="Could not mark as complete">
            {markWiredUp.error instanceof Error
              ? markWiredUp.error.message
              : "Try again."}
          </Message>
        )}
        <Button.Group style={{ display: "flex", width: "100%", marginTop: 24 }}>
          <Button
            type="button"
            active={setupMode === "auto"}
            style={{ flex: 1 }}
            onClick={() => setSetupMode("auto")}
          >
            <Icon icon={faSparkles} marginRight />
            Auto setup
          </Button>
          <Button
            type="button"
            active={setupMode === "manual"}
            style={{ flex: 1 }}
            onClick={() => setSetupMode("manual")}
          >
            <Icon icon={faGear} marginRight />
            Manual setup
          </Button>
        </Button.Group>
        {setupMode === "auto" && detail && (
          <div className="client-wireup-auto">
            <p className="bf-p bf-large bfc-base-2" style={{ margin: 0 }}>
              Paste this prompt into your AI model in your project and it wires
              the client up for you.
            </p>
            <pre className="client-wireup-prompt">{prompt}</pre>
            <Button
              variant="basic"
              onClick={copyPrompt}
              style={{ alignSelf: "flex-end" }}
            >
              <Icon icon={promptCopied ? faCheck : faCopy} marginRight />
              {promptCopied ? "Copied" : "Copy AI prompt"}
            </Button>
            {/* The prompt has the AI model register a SPA's redirect URL
                itself when it has the MCP server; when it does not, this is
                where the URL it hands back goes. Pink, the SPA badge's
                colour. */}
            {isSpa && (
              <Message state="attn">
                If your AI model cannot add the redirect URL itself, paste the
                URL it gives you into the client to finish the wire up.
              </Message>
            )}
          </div>
        )}
        {setupMode === "manual" && wireUpClient && (
          <div style={{ marginTop: 16 }}>
            <ClientWireUpSteps steps={buildManualSteps(wireUpClient)} />
          </div>
        )}
      </Drawer>
    </Section>
  );
}
